import React from 'react';
import PropTypes from 'prop-types';
import {
  cityLabel,
  namesOf,
  useSelectOptions,
} from '../../../helpers/selectOptions';
import ReactiveTable from '../../Organisms/ReactiveTable/ReactiveTable';
import RecordDetails from '../../molecules/RecordDetails/RecordDetails';

export const inventionColumns = [
  {
    name: 'Code',
    selector: row => row.code,
    sortable: true,
    width: '100px',
  },
  {
    name: 'Invention',
    selector: row => row.name,
    sortable: true,
    wrap: true,
  },
  {
    name: 'Kind',
    selector: row => namesOf(row.kinds),
    sortable: true,
    wrap: true,
  },
  {
    name: 'Score',
    selector: row => row.score,
    sortable: true,
    width: '90px',
  },
  {
    name: 'XR Object',
    selector: row => (row.xr_object ? 'Yes' : 'No'),
    sortable: true,
    width: '110px',
  },
];

// Select options for the invention form, shared with the scholar's inventions table
export const useInventionOptions = () => ({
  scholars: useSelectOptions('types/scholars?per_page=200'),
  kinds: useSelectOptions('kinds?per_page=500', kind => kind.name),
  complexities: useSelectOptions('complexities?per_page=500', complexity => complexity.name),
  contexts: useSelectOptions('types/contexts?per_page=200'),
  products: useSelectOptions('types/products?per_page=200'),
  cities: useSelectOptions('types/cities?per_page=500', cityLabel),
  dynasties: useSelectOptions('types/dynasties?per_page=200'),
});

// options: from useInventionOptions; defaultScholarIds: preselected scholars for new inventions
export const inventionFields = (options, defaultScholarIds = undefined) => [
  'code',
  'name',
  {
    key: 'scholars_ids',
    type: 'multiselect',
    options: options.scholars,
    valueFrom: invention => invention.scholars.map(item => item.id),
    defaultValue: defaultScholarIds,
  },
  {
    key: 'kinds_ids',
    type: 'multiselect',
    options: options.kinds,
    valueFrom: invention => invention.kinds.map(item => item.id),
    createEndpoint: 'kinds',
  },
  { key: 'score', type: 'number' },
  {
    key: 'complexities_ids',
    type: 'multiselect',
    options: options.complexities,
    valueFrom: invention => invention.complexities.map(item => item.id),
    createEndpoint: 'complexities',
  },
  { key: 'description', type: 'textarea' },
  {
    key: 'contexts_ids',
    type: 'multiselect',
    options: options.contexts,
    valueFrom: invention => invention.contexts.map(item => item.id),
  },
  {
    key: 'products_ids',
    type: 'multiselect',
    options: options.products,
    valueFrom: invention => invention.products.map(item => item.id),
  },
  {
    key: 'locations_ids',
    type: 'multiselect',
    options: options.cities,
    valueFrom: invention => invention.locations.map(item => item.id),
  },
  {
    key: 'dynasties_ids',
    type: 'multiselect',
    options: options.dynasties,
    valueFrom: invention => invention.dynasties.map(item => item.id),
  },
  {
    key: 'status',
    type: 'select',
    options: [
      { label: 'Original', value: 'original' },
      { label: 'Added', value: 'added' },
      { label: 'Replaced', value: 'replaced' },
    ],
  },
  {
    key: 'xr_object',
    type: 'select',
    options: [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }],
  },
];

const InventionDetails = ({ data }) => (
  <RecordDetails
    items={[
      { label: 'Description', value: data.description },
      { label: 'Complexity', value: namesOf(data.complexities) },
      { label: 'Contexts', value: namesOf(data.contexts) },
      { label: 'Products', value: namesOf(data.products) },
      { label: 'Locations', value: namesOf(data.locations) },
      { label: 'Countries', value: data.country_codes },
      { label: 'Dynasties', value: namesOf(data.dynasties) },
      { label: 'Status', value: data.status },
    ]}
  />
);

InventionDetails.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
};

const InventionsPage = () => {
  const options = useInventionOptions();
  const columns = [
    ...inventionColumns.slice(0, 2),
    {
      name: 'Scholars',
      selector: row => namesOf(row.scholars) || '-',
      sortable: true,
      wrap: true,
    },
    ...inventionColumns.slice(2),
  ];
  return (
    <>
      <div className="col-md-12 header-container">
        <h2>Inventions</h2>
      </div>
      <div className="col-md-12 content-container">
        <ReactiveTable
          columns={columns}
          endpoint="types/inventions"
          expandedComponent={InventionDetails}
          exportableKeys={['code', 'name', 'score', 'description', 'country_codes', 'status', 'xr_object']}
          modalTitle="Invention"
          fields={inventionFields(options)}
        />
      </div>
    </>
  );
};

export default InventionsPage;
